import React, { useContext, useEffect, useState } from 'react';
import { getAllUsers } from '@juno/client-api';
import { JunoUser, UserSettings } from '@juno/client-api/model';
import { JunoUserEnhanced, MessengerContext, MessengerMap, useSettings } from '@juno/utils';
import { PlatformContext } from '../Contexts/PlatformContext';
import { usePubnubContext } from '../Contexts/PubnubContext';
import { ChannelToUnreadMessageCountMap } from './Utils';

type MessengerProviderProps = {
  children: React.ReactNode;
};

export const MessengerProvider: React.FC<MessengerProviderProps> = ({ children }) => {
  const platformContext = useContext(PlatformContext);
  const {
    subscribeToChannels,
    systemChannel,
    messages,
    channelMemberUuids,
    subscribedChannels,
    sendMessage,
    createChannelThenSubscribe,
    markLastRead,
    hideChannel,
    hiddenChannels,
    updatedChannelMembers,
  } = usePubnubContext();

  // Messenger Data
  const { user, site } = useSettings();
  const [junoUsersEnhanced, setJunoUsersEnhanced] = useState<JunoUserEnhanced[]>([]);
  const [messengerMap, setMessengerMap] = useState<MessengerMap>({ channels: [] });
  const maxChannelUsers = 10; // Const for the max number of users in a channel... might need tuning but used in more than one place so I put it here as const

  // Used to manage of the Messenger UI
  const [isConversation, setIsConversation] = useState(false);
  const [selectedNewConversationUsers, setSelectedNewConversationUsers] = useState<JunoUser[]>([]);
  const [messengerTab, setMessengerTab] = React.useState(0);
  const [isCreateNewConversation, setIsCreateNewConversation] = useState(false);
  const [currentChannel, setCurrentChannel] = React.useState('');
  const [unreadMessageCounts, setUnreadMessageCounts] = useState<ChannelToUnreadMessageCountMap>(
    {},
  );
  const [currentConversationLastReadMessageId, setCurrentConversationLastReadMessageId] =
    React.useState('');

  const sendMessageToChannel = (message: string) => {
    sendMessage(currentChannel, message).then(() => {});
  };

  useEffect(() => {
    if (!site?.platform_id) return;

    // Wrap your async logic in a function
    const fetchUsers = async () => {
      const allUuids = Object.entries(channelMemberUuids).reduce(
        (acc, [channelId, uuids]) => [...acc, ...uuids],
        [] as string[],
      );

      const uniqueUuids = [...new Set(allUuids)];

      // Await is allowed here (inside an async function)
      const users = await getAllUsers(site.platform_id, {
        filter_or: { id__in: uniqueUuids },
        limit: 50,
        offset: 0,
        include: 'settings',
      });

      const junoUsersEnhanced = users.map((user) => {
        const castSettings = (user as any).settings as Partial<UserSettings> | undefined;

        return {
          ...user,
          hidden_email: castSettings?.hidden_email ?? false,
          private_profile: castSettings?.private_profile ?? false,
          disabled_private_messaging: castSettings?.disabled_private_messaging ?? false,
          receive_daily_digest: castSettings?.receive_daily_digest ?? false,
        };
      });

      const newMessengerMap: MessengerMap = {
        channels: subscribedChannels.map((channel) => {
          // Use optional chaining or a fallback array to avoid errors if undefined:
          const memberIds = channelMemberUuids[channel] ?? [];

          return {
            id: channel,
            name: channel,
            userIds: memberIds,
            messages: messages[channel] ?? [],
            isHidden: hiddenChannels.includes(channel),
            junoUsers: junoUsersEnhanced.filter((u) => memberIds.includes(u.id)),
          };
        }),
      };

      setMessengerMap(newMessengerMap);
    };

    fetchUsers(); // call the async function
  }, [messages, subscribedChannels, hiddenChannels, updatedChannelMembers]);

  const markRead = (channelId: string, timetoken?: string) => {
    return markLastRead(channelId);
  };

  const removeUserFromChannel = (channelId: string, userId: string) => {
    // return empty promise for now
    return Promise.resolve(false);
  };

  const setChannelVisibility = (channelId: string, userId: string, hide: boolean) => {
    return hideChannel(channelId, hide);
  };

  const startConversation = (userIds: string[]) => {
    return createChannelThenSubscribe(userIds).then((channelId) => {
      return channelId;
    });
  };

  const contextValue = {
    isConversation,
    setIsConversation,
    messengerTab,
    setMessengerTab,
    currentChannel,
    setCurrentChannel: (channelId: any) => {
      // console.log('setCurrentChannel called with:', channelId);
      setCurrentChannel(channelId); // You can also use a functional update if needed
    },
    maxChannelUsers,
    unreadMessageCounts,
    setUnreadMessageCounts,
    systemChannel,
    markRead,
    removeUserFromChannel,
    setChannelVisibility,
    messengerMap,
    startConversation,
    subscribeToChannels,
    isCreateNewConversation,
    setIsCreateNewConversation,
    currentConversationLastReadMessageId,
    setCurrentConversationLastReadMessageId,
    selectedNewConversationUsers,
    setSelectedNewConversationUsers,
    sendMessageToChannel,
  };

  return <MessengerContext.Provider value={contextValue}>{children}</MessengerContext.Provider>;
};

export default MessengerProvider;
